import type { CallControlAction, NativeCanonicalName, ToolFamily } from "./tool-families";
import type { AgentTool } from "@/schemas/agent.schema";
import type { FlexComponent } from "@/utils/render-component";
import { startCase } from "lodash-es";
import MCP from "@/icons/svgs/MCP";
import { $t } from "@/services/i18n";
import { CALL_CONTROL_ACTIONS, classifyToolCall, NATIVE_CANONICAL_NAMES } from "./tool-families";

export const NATIVE_TITLE_KEYS = {
  web_search: "agents:builtin_tools.tools.web_search",
  web_fetch: "agents:builtin_tools.tools.web_fetch",
  calculator: "agents:builtin_tools.tools.calculator",
  current_datetime: "agents:builtin_tools.tools.current_datetime",
  code_execution: "agents:builtin_tools.tools.code_execution",
} as const;

const CALL_CONTROL_ICONS: Record<CallControlAction, string> = {
  end_dialog: "lucide:phone-off",
  transfer_call: "lucide:phone-forwarded",
  send_dtmf: "lucide:phone-outgoing",
  receive_dtmf: "lucide:grid-3x3",
};

const NATIVE_ICONS: Record<NativeCanonicalName, string> = {
  web_search: "lucide:search",
  web_fetch: "lucide:globe",
  calculator: "lucide:calculator",
  current_datetime: "lucide:calendar",
  code_execution: "lucide:terminal",
};

/** Pre-AI-1195 per-extension rag icons, now resolved from the family payload. */
function ragFileIcon(fileName: string | undefined): string {
  const extension = fileName?.split(".").pop()?.toLowerCase();
  if (extension === "pdf") return "lucide:file-type-2";
  if (extension === "docx") return "lucide:file-type";
  if (extension === "txt" || extension === "md") return "lucide:file-text";
  if (extension === "json") return "lucide:braces";
  if (extension === "csv") return "lucide:file-spreadsheet";
  return "lucide:file";
}

/** AI-1195: one icon per family, so chat cards and tool lists tell the same story. */
export function getFamilyIcon(family: ToolFamily): string | FlexComponent {
  switch (family.kind) {
    case "discovery":
      return "lucide:search";
    case "agent_tool":
      if (family.agentType === "mcp") return MCP;
      if (family.agentType === "api") return "lucide:atom";
      if (family.agentType === "knowledge") return "lucide:database";
      if (family.agentType === "rag") return ragFileIcon(family.rag.fileName);
      return "lucide:plug";
    case "native":
      return NATIVE_ICONS[family.canonical];
    case "call_control":
      return CALL_CONTROL_ICONS[family.action];
    case "escalation":
      return "lucide:circle-help";
    case "unknown":
      return "lucide:wrench";
  }
}

export function nativeTitle(canonical: NativeCanonicalName): string {
  return $t(NATIVE_TITLE_KEYS[canonical]);
}

export function isNativeCanonicalName(value: string): value is NativeCanonicalName {
  return (NATIVE_CANONICAL_NAMES as readonly string[]).includes(value);
}

export function isCallControlAction(value: string): value is CallControlAction {
  return (CALL_CONTROL_ACTIONS as readonly string[]).includes(value);
}

export function getToolIcon(tool: AgentTool): string | FlexComponent {
  return getFamilyIcon(classifyToolCall(tool.tool.name, [tool]));
}

export function getToolName(tool: AgentTool): string {
  const family = classifyToolCall(tool.tool.name, [tool]);
  if (family.kind === "native") return nativeTitle(family.canonical);

  return startCase(tool.tool.name);
}
